import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Bell,
  Users,
  Calendar,
  CheckCircle,
  TrendingUp,
  RefreshCw,
  LogOut,
  Phone,
  Mail,
  Star,
  Award,
  Target,
  MessageSquare,
  MessageCircle,
  CreditCard,
  Settings,
  Home,
  Lightbulb,
  Trophy,
  ArrowUp,
  ArrowDown,
  Clock,
  Euro,
  MapPin,
  FileText,
  ChevronDown,
  HelpCircle,
  Menu,
  BarChart3,
  User,
  Camera,
} from "lucide-react";
import { agentAdvancerMain } from "./images";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AgencyFeedManager from "./components/AgencyFeedManager";
import AgencyNotifications from "./components/AgencyNotifications";
import MatchAgenzia from "./components/MatchAgenzia";
import FormazioneView from "./components/FormazioneView";
import GestisciMatchContainer from "./components/GestisciMatchContainer";
import FeedbackRiepilogo from "./components/FeedbackRiepilogo";
import ColloquiList from "./components/ColloquiList";
import { BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color scheme per il design moderno
const colors = {
  primary: "#1e293b", // Blue night
  accent: "#10b981", // Green brilliant
  purple: "#8b5cf6", // Purple
  background: "#f8fafc",
  cardBg: "#ffffff",
  text: "#334155",
  textLight: "#64748b",
  success: "#059669",
  warning: "#d97706",
  danger: "#dc2626",
};

const DashboardAgenzia = ({ agenziaId: propAgenziaId }) => {
  const { agenziaId: paramAgenziaId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Access control: blocca se non agenzia o id diverso
  useEffect(() => {
    // Usa impersonated_token se presente
    const token =
      localStorage.getItem("impersonated_token") ||
      localStorage.getItem("token");
    console.log("[DEBUG] Token usato in DashboardAgenzia:", token);
    if (!token || token === "null" || token === null) {
      toast.error(
        "Token di accesso non trovato. Ricarica la pagina o torna al pannello admin."
      );
      window.location.href = "/#/admin";
      return;
    }
    let userId = null,
      userType = null;
    try {
      const decoded = jwtDecode(token);
      userId = decoded.user_id;
      userType = decoded.user_type;
    } catch (err) {
      console.error("Errore decodifica token:", err);
    }
    const rawAgenziaId =
      propAgenziaId ||
      paramAgenziaId ||
      searchParams.get("agenzia_id") ||
      "test-agenzia-demo";
    const agenziaIdCheck =
      typeof rawAgenziaId === "string" && !isNaN(rawAgenziaId)
        ? parseInt(rawAgenziaId)
        : rawAgenziaId;
    console.log(
      "[DEBUG] userType:",
      userType,
      "userId:",
      userId,
      "agenziaIdCheck:",
      agenziaIdCheck
    );
    if (userType !== "agenzia" || String(userId) !== String(agenziaIdCheck)) {
      const debugMsg = `[ACCESS BLOCKED]\nuserType: ${userType}\nuserId: ${userId}\nagenziaIdCheck: ${agenziaIdCheck}`;
      console.warn(debugMsg);
    }
  }, [propAgenziaId, paramAgenziaId, searchParams]);

  const [loading, setLoading] = useState(false);
  const [agenzia, setAgenzia] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeBottomTab, setActiveBottomTab] = useState("home");
  const [showDropdown, setShowDropdown] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState(null);
  const [currentBreadcrumb, setCurrentBreadcrumb] = useState("Dashboard");

  // Converti agenziaId in numero per evitare problemi di confronto
  const rawAgenziaId =
    propAgenziaId ||
    paramAgenziaId ||
    searchParams.get("agenzia_id") ||
    "test-agenzia-demo";
  const agenziaId =
    typeof rawAgenziaId === "string" && !isNaN(rawAgenziaId)
      ? parseInt(rawAgenziaId)
      : rawAgenziaId;

  // Instagram-style bottom navigation items (max 5 per UI ottimale)
  const bottomNavItems = [
    { key: "home", icon: Home, label: "Home" },
    { key: "gestisci-match", icon: Users, label: "Match" },
    { key: "colloqui", icon: MessageCircle, label: "Colloqui" },
    { key: "formazione", icon: BookOpen, label: "Formazione" },
    { key: "notifiche", icon: Bell, label: "Notifiche" },
  ];

  // Dropdown menu items (menu hamburger)
  const dropdownItems = [
    { key: "dashboard", label: "Dashboard principale", icon: Home },
    { key: "feedback-riepilogo", label: "Feedback Ricevuti", icon: Star },
    { key: "messaggi", label: "Messaggi ricevuti", icon: MessageSquare },
    { key: "statistiche", label: "Statistiche attività", icon: BarChart3 },
    { key: "assistenza", label: "Contatta assistenza", icon: HelpCircle },
    { key: "impostazioni", label: "Impostazioni agenzia", icon: Settings },
    { key: "logout", label: "Logout", icon: LogOut },
  ];

  // No more mock data - use real API data only

  const subscriptionPlans = [
    {
      name: "START ZONE",
      price: "€160/mese per zona",
      period: "Durata minima: 3 mesi",
      guarantee: "3 colloqui junior garantiti",
      description: "Prova iniziale senza rischi",
      features: [
        "3 colloqui junior garantiti",
        "Più colloqui possibili senza costi extra",
        "Prova iniziale senza rischi",
      ],
      color: "#38bdf8",
      current: false,
    },
    {
      name: "FLUSSO ATTIVO",
      price: "€300/mese per zona",
      period: "Durata minima: 3 mesi",
      guarantee: "3 colloqui misti garantiti",
      description: "Per una crescita sostenuta",
      features: [
        "3 colloqui misti garantiti",
        "Più colloqui possibili senza costi extra",
        "Estensione gratuita se non si raggiunge il minimo",
        "Per una crescita sostenuta",
      ],
      color: "#10b981",
      current: true,
    },
    {
      name: "TEAM ESPERTO",
      price: "€749/mese per zona",
      period: "Durata minima: 3 mesi",
      guarantee: "3 colloqui senior garantiti",
      description: "Per agenzie strutturate",
      features: [
        "3 colloqui senior garantiti",
        "Più colloqui possibili senza costi extra",
        "Estensione gratuita se non si raggiunge il minimo",
        "Per agenzie strutturate",
      ],
      color: "#8b5cf6",
      current: false,
    },
  ];

  useEffect(() => {
    if (agenziaId) {
      loadAgenziaData();

      // Check for impersonation session
      const sessionId = searchParams.get("session");
      if (sessionId) {
        loadImpersonationSession(sessionId);
      }
    }
  }, [agenziaId, navigate]);

  const loadAgenziaData = async () => {
    try {
      setLoading(true);

      // Usa impersonated_token se presente
      const token =
        localStorage.getItem("impersonated_token") ||
        localStorage.getItem("token");
      if (!token) {
        console.warn("Nessun token trovato, reindirizzamento al login");
        navigate("/login");
        return;
      }

      // Carica dati agenzia
      const agenziaResponse = await fetch(`${API}/v1/agenzie/${agenziaId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (agenziaResponse.ok) {
        const agenziaData = await agenziaResponse.json();
        setAgenzia(agenziaData);
      } else if (agenziaResponse.status === 403) {
        console.error("Accesso negato - 403 Forbidden");
        toast.error("Accesso negato");
      } else if (agenziaResponse.status === 401) {
        console.error(
          "[DEBUG 401] Chiamata API:",
          `${API}/v1/agenzie/${agenziaId}`
        );
        agenziaResponse
          .text()
          .then((txt) => console.error("[DEBUG 401] Response:", txt));
        toast.error("Non autenticato - 401 Unauthorized");
        navigate("/login");
      }

      // Dashboard data inizializzato vuoto - si popolerà con dati reali dal backend
      setDashboardData({
        performance: {
          colloquiMensili: 0,
          inserimentiMensili: 0,
          tempoMedioRisposta: 0,
          tassoConversione: 0,
        },
        ranking: {
          posizione: 0,
          totalAgenzie: 0,
          punteggio: 0,
          trend: "0%",
        },
        obiettivi: {},
        messaggi: [],
        intervisteAgenda: [],
      });
    } catch (error) {
      console.error("Errore nel caricamento dati:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const loadImpersonationSession = async (sessionId) => {
    try {
      const token =
        localStorage.getItem("impersonated_token") ||
        localStorage.getItem("token");
      console.log("[DEBUG] Token usato per impersonation:", token);
      const response = await fetch(
        `${API}/v1/admin/impersonation/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const responseText = await response.text();
      console.log(
        "[DEBUG] Risposta fetch impersonation:",
        response.status,
        responseText
      );
      if (response.ok) {
        const sessionData = JSON.parse(responseText);
        setImpersonationSession(sessionData);
      } else {
        console.error(
          "Errore impersonation session:",
          response.status,
          responseText
        );
      }
    } catch (error) {
      console.error("Errore nel caricamento sessione impersonation:", error);
    }
  };

  const handleReturnToAdmin = () => {
    if (impersonationSession) {
      // Termina la sessione di impersonation
      fetch(
        `${API}/v1/admin/impersonation/${impersonationSession.session_id}`,
        {
          method: "DELETE",
        }
      ).catch((error) => console.error("Error ending impersonation:", error));
    }
    // Reindirizza al pannello admin
    window.location.href = "/#/admin-test"; // Use test route until routing is fixed
  };

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    localStorage.removeItem("agenziaId");

    // Show success message
    toast.success("Logout effettuato con successo");

    // Redirect to login page
    navigate("/login");
  };

  // Update breadcrumb when tab changes
  useEffect(() => {
    const breadcrumbMap = {
      home: "Dashboard",
      match: "Match Attivi",
      "gestisci-match": "Gestisci Match & Colloqui",
      colloqui: "Gestione Colloqui & Feedback",
      "feedback-riepilogo": "Feedback Ricevuti",
      formazione: "Formazione & Team",
      notifiche: "Notifiche",
      posts: "I tuoi Post",
      candidati: "Candidati",
      messaggi: "Messaggi",
      impostazioni: "Impostazioni",
    };
    setCurrentBreadcrumb(breadcrumbMap[activeBottomTab] || "Dashboard");
  }, [activeBottomTab]);

  const [showAssistanceModal, setShowAssistanceModal] = useState(false);
  const [assistanceMessage, setAssistanceMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatReply, setChatReply] = useState("");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedAgenzia, setEditedAgenzia] = useState(null);

  const handleSubscriptionAction = (plan) => {
    setSelectedPlan(plan);
    setShowSubscriptionModal(true);
  };

  const handleSendAssistanceMessage = async () => {
    if (!assistanceMessage.trim()) return;

    try {
      // Simulazione invio messaggio assistenza
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Messaggio inviato al team di assistenza!");
      setAssistanceMessage("");
      setShowAssistanceModal(false);
    } catch (error) {
      toast.error("Errore nell'invio del messaggio");
    }
  };

  const handleSendChatReply = async () => {
    if (!chatReply.trim()) return;

    try {
      // Simulazione invio risposta
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Risposta inviata!");
      setChatReply("");
      setShowChatModal(false);
    } catch (error) {
      toast.error("Errore nell'invio della risposta");
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      // Simulazione salvataggio profilo
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setAgenzia(editedAgenzia);
      setIsEditingProfile(false);
      toast.success("Profilo aggiornato con successo!");
    } catch (error) {
      toast.error("Errore nel salvataggio del profilo");
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownAction = (action) => {
    console.log(`ACTION: ${action}`);
    setShowDropdown(false);

    switch (action) {
      case "dashboard":
        setActiveBottomTab("home");
        setCurrentBreadcrumb("Dashboard");
        toast.success("Dashboard principale");
        break;
      case "messaggi":
        setActiveBottomTab("messaggi");
        setCurrentBreadcrumb("Dashboard > Messaggi");
        toast.success("Messaggi ricevuti");
        break;
      case "abbonamenti":
        setActiveBottomTab("abbonamento");
        setCurrentBreadcrumb("Dashboard > Gestione Abbonamenti");
        toast.success("Gestione abbonamenti");
        break;
      case "statistiche":
        // Creo una nuova sezione per statistiche o uso home con focus su charts
        setActiveBottomTab("home");
        setCurrentBreadcrumb("Dashboard > Statistiche Attività");
        toast.success("Statistiche attività");
        break;
      case "assistenza":
        setShowAssistanceModal(true);
        setCurrentBreadcrumb("Dashboard > Assistenza");
        toast.success("Contatta assistenza");
        break;
      case "impostazioni":
        setActiveBottomTab("impostazioni");
        setCurrentBreadcrumb("Dashboard > Impostazioni Agenzia");
        toast.success("Impostazioni agenzia");
        break;
      case "logout":
        toast.info("Disconnessione...");
        navigate(impersonationSession ? "/#/admin-test" : "/login");
        break;
      default:
        console.log(`Azione non riconosciuta: ${action}`);
    }
  };

  const renderHomeContent = () => (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-blue-100 text-xs sm:text-sm truncate">
                  Colloqui Mese
                </p>
                <p className="text-lg sm:text-2xl font-bold">
                  {dashboardData?.performance?.colloquiMensili || 0}
                </p>
                <script></script>
              </div>
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-blue-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-green-100 text-xs sm:text-sm truncate">
                  Inserimenti
                </p>
                <p className="text-lg sm:text-2xl font-bold">
                  {dashboardData?.performance?.inserimentiMensili || 0}
                </p>
              </div>
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white border-0">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-purple-100 text-xs sm:text-sm truncate">
                  Ranking
                </p>
                <p className="text-lg sm:text-2xl font-bold">
                  #{dashboardData?.ranking?.posizione || 0}
                </p>
              </div>
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-purple-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-600 to-orange-700 text-white border-0">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-orange-100 text-xs sm:text-sm truncate">
                  Tempo Risp.
                </p>
                <p className="text-lg sm:text-2xl font-bold">
                  {dashboardData?.performance?.tempoMedioRisposta || 0}h
                </p>
              </div>
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Azioni Rapide */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Azioni Rapide</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              onClick={() => setActiveBottomTab("gestisci-match")}
              className="h-20 flex flex-col items-center gap-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              variant="outline"
            >
              <Users className="w-6 h-6" />
              <span>Gestisci Match</span>
            </Button>

            <Button
              onClick={() => setActiveBottomTab("messaggi")}
              className="h-20 flex flex-col items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
              variant="outline"
            >
              <MessageSquare className="w-6 h-6" />
              <span>Messaggi</span>
            </Button>

            <Button
              onClick={() => setActiveBottomTab("posts")}
              className="h-20 flex flex-col items-center gap-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              variant="outline"
            >
              <FileText className="w-6 h-6" />
              <span>Crea Post</span>
            </Button>

            <Button
              onClick={() => setActiveBottomTab("notifiche")}
              className="h-20 flex flex-col items-center gap-2 bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
              variant="outline"
            >
              <Bell className="w-6 h-6" />
              <span>Notifiche</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Performance Attività
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Dati performance in arrivo</p>
            <p className="text-sm mt-1">
              I grafici si popolano automaticamente con le tue attività
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Insights & AI Suggestions - Ranking temporaneamente nascosto per fase beta */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-600" />
            Insights & Suggerimenti IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* AI Suggestions */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border-l-4 border-yellow-400">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    💡 Suggerimento IA
                  </h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Aumenta la tua velocità di risposta per migliorare
                    l'engagement! Target consigliato: &lt;1 ora
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Target className="w-4 h-4" />
                    <span>
                      Obiettivo: Ridurre il tempo medio di risposta del 20%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Insights */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                <h5 className="font-medium text-blue-900 mb-1">
                  📊 Tendenza Colloqui
                </h5>
                <p className="text-sm text-blue-700">
                  +15% rispetto al mese scorso
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                <h5 className="font-medium text-green-900 mb-1">
                  ✅ Inserimenti
                </h5>
                <p className="text-sm text-green-700">
                  Tasso conversione: 38.7%
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ranking Agenzie - Disponibile presto */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" />
            Ranking Agenzie
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Ranking in arrivo</p>
            <p className="text-sm mt-1">
              Il sistema di classifiche sarà attivato a breve
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Obiettivi Mensili */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Obiettivi Mensili
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.keys(dashboardData?.obiettivi || {}).length > 0 ? (
              Object.entries(dashboardData.obiettivi).map(([key, obj]) => (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="capitalize font-medium">{key}</span>
                    <span className="text-sm text-gray-600">
                      {obj.attuale}/{obj.target} ({obj.percentuale.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(obj.percentuale, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Obiettivi in definizione</p>
                <p className="text-sm mt-1">
                  I tuoi obiettivi mensili verranno configurati presto
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Agenda Interviste */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Agenda Interviste
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData?.intervisteAgenda?.length > 0 ? (
                dashboardData.intervisteAgenda.slice(0, 3).map((intervista) => (
                  <div
                    key={intervista.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="font-medium text-sm">
                        {intervista.candidato}
                      </div>
                      <div className="text-xs text-gray-600">
                        {intervista.tipo}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {intervista.ora}
                      </div>
                      <div className="text-xs text-gray-600">
                        {intervista.data}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun appuntamento</p>
                  <p className="text-sm mt-1">
                    Le tue interviste programmate appariranno qui
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Messaggi Recenti */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Messaggi Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboardData?.messaggi?.length > 0 ? (
                dashboardData.messaggi.slice(0, 3).map((messaggio) => (
                  <div
                    key={messaggio.id}
                    onClick={() => {
                      setSelectedMessage(messaggio);
                      setShowChatModal(true);
                    }}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${
                        messaggio.letto ? "bg-gray-300" : "bg-blue-500"
                      }`}
                    ></div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{messaggio.da}</div>
                      <div className="text-xs text-gray-600 truncate">
                        {messaggio.preview}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {messaggio.tempo}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun messaggio</p>
                  <p className="text-sm mt-1">
                    I messaggi dai candidati appariranno qui
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderCandidatiContent = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Gestione Candidati
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Funzionalità candidati in arrivo...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderMessaggiContent = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Centro Messaggi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {dashboardData?.messaggi?.map((messaggio) => (
              <div
                key={messaggio.id}
                onClick={() => {
                  setSelectedMessage(messaggio);
                  setSzhowChatModal(true);
                }}
                className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div
                  className={`w-4 h-4 rounded-full mt-1 ${
                    messaggio.letto ? "bg-gray-300" : "bg-blue-500"
                  }`}
                ></div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium">{messaggio.da}</h4>
                    <span className="text-xs text-gray-500">
                      {messaggio.tempo}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{messaggio.preview}</p>
                  <div className="mt-2">
                    <Button size="sm" variant="outline" className="text-xs">
                      Rispondi
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAbbonamentoContent = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            Gestione Abbonamenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            {subscriptionPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative p-6 rounded-2xl border-2 transition-all hover:shadow-lg ${
                  plan.current
                    ? "border-green-500 bg-green-50 shadow-lg"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                {plan.current && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                      Piano Attuale
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span
                      className="text-3xl font-bold"
                      style={{ color: plan.color }}
                    >
                      {plan.price}
                    </span>
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div className="mb-1">
                      Durata:{" "}
                      <span className="font-medium">{plan.duration}</span>
                    </div>
                    <div className="mb-1 text-green-700 font-medium">
                      {plan.guarantee}
                    </div>
                    <div className="text-blue-600 italic">
                      {plan.description}
                    </div>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscriptionAction(plan)}
                  className={`w-full ${
                    plan.current
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-gray-900 hover:bg-gray-800"
                  }`}
                >
                  {plan.current
                    ? "Rinnova Piano"
                    : plan.name === "ELITE"
                    ? "Upgrada"
                    : "Scegli Piano"}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderImpostazioniContent = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600" />
              Impostazioni Agenzia
            </div>
            {!isEditingProfile ? (
              <Button
                onClick={() => {
                  setEditedAgenzia({ ...agenzia });
                  setIsEditingProfile(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Modifica Profilo
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditingProfile(false)}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? "Salvando..." : "Salva"}
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Informazioni Agenzia */}
            <div>
              <h4 className="font-medium mb-3">Informazioni Agenzia</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Campi principali */}
                {[
                  ["Nome Agenzia", "nome"],
                  ["Zona", "zone_di_interesse"],
                  ["Posti Disponibili", "posti_disponibili"],
                  ["Profilo Ricercato", "profilo_ricercato"],
                  ["Referente", "contatto_referente"],
                  ["Telefono Referente", "telefono_referente"],
                  ["Email Referente", "email_referente"],
                  ["Email", "email"],
                  ["Telefono", "telefono"],
                  ["Indirizzo", "indirizzo"],
                  ["Città", "citta"],
                  ["CAP", "cap"],
                  ["Provincia", "provincia"],
                  ["Partita IVA", "partita_iva"],
                  ["Codice Fiscale", "codice_fiscale"],
                  ["Sito Web", "sito_web"],
                  ["Numero Dipendenti", "numero_dipendenti"],
                  // ["Anni Esperienza", "anni_esperienza"],
                  // ["Zone di Interesse", "zone_di_interesse"],
                  // ["Stato", "stato"],
                  // ["Note", "note"],
                  ["Descrizione", "descrizione"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={editedAgenzia?.[key] || ""}
                        onChange={(e) =>
                          setEditedAgenzia((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        {agenzia?.[key] !== undefined && agenzia?.[key] !== null && agenzia?.[key] !== ""
                          ? agenzia[key]
                          : <span className="text-gray-400 italic">Non disponibile</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cambio Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Sicurezza Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChangePasswordSection agenziaId={agenziaId} />
        </CardContent>
      </Card>
    </div>
  );

  const renderContent = () => {
    switch (activeBottomTab) {
      case "match":
        return <MatchAgenzia agencyId={agenziaId} />;
      case "gestisci-match":
        return <GestisciMatchContainer agencyId={agenziaId} />;
      case "colloqui":
        return <ColloquiList userType="agenzia" />;
      case "feedback-riepilogo":
        return <FeedbackRiepilogo agencyId={agenziaId} />;
      case "formazione":
        return (
          <FormazioneView visibilita="agenzie" titolo="Formazione & Team" />
        );
      case "candidati":
        return renderCandidatiContent();
      case "messaggi":
        return renderMessaggiContent();
      case "notifiche":
        return <AgencyNotifications agenziaId={agenziaId} />;
      case "posts":
        return (
          <AgencyFeedManager
            agenziaId={agenziaId}
            agenziaNome={agenzia?.nome_agenzia}
          />
        );
      case "impostazioni":
        return renderImpostazioniContent();
      default:
        return renderHomeContent();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation Banner */}
      {impersonationSession && (
        <ImpersonationBanner
          userName={
            impersonationSession.impersonated_user_name || agenzia?.nome || "Agenzia"
          }
          userType="agenzia"
          onReturnToAdmin={handleReturnToAdmin}
        />
      )}

      {/* Demo Mode Banner */}
      {agenzia && agenzia.tipo_account === "demo" && (
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold">🆓 Modalità Demo Attiva</span>
              <span className="hidden sm:inline">
                • Stai usando la versione demo. Alcune funzioni premium sono
                disabilitate.
              </span>
            </div>
            <button
              onClick={() => setActiveBottomTab("gestione")}
              className="bg-white text-orange-600 px-4 py-1.5 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Passa ai piani a pagamento
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 justify-around">
              <button
                onClick={() => setActiveBottomTab("home")}
                className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 flex-shrink-0 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                title="Torna alla Dashboard"
              >
                <img
                  src={agentAdvancerMain}
                  alt="InLeads"
                  className="w-6 h-6 sm:w-8 sm:h-8"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML =
                      '<span class="text-gray-700 font-bold text-xs">IL</span>';
                  }}
                />
              </button>

              {/* Desktop Horizontal Navigation - Solo Desktop */}
              <nav className="hidden lg:flex items-center gap-1 ml-8">
                <button
                  onClick={() => setActiveBottomTab("home")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "home"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <Home className="w-4 h-4" />
                  <span className="text-sm">Home</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("gestisci-match")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "gestisci-match"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Match</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("formazione")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "formazione"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span className="text-sm">Formazione</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("messaggi")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "messaggi"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm">Messaggi</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("notifiche")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "notifiche"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">Notifiche</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("posts")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "posts"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600"
                  }`}
                >
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">Feed</span>
                </button>

                <button
                  onClick={() => setActiveBottomTab("feedback-riepilogo")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeBottomTab === "feedback-riepilogo"
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                  }`}
                >
                  <Star className="w-4 h-4" />
                  <span className="text-sm">Feedback</span>
                </button>
              </nav>
              {/* Header Navigation */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Menu Hamburger - Solo Mobile */}
                <div className="relative lg:hidden">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log("DROPDOWN TOGGLE");
                      setShowDropdown(!showDropdown);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    className="flex items-center gap-1 sm:gap-2 p-2 sm:p-3 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-gray-200"
                    style={{
                      minHeight: "48px",
                      minWidth: "48px",
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    <Menu className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    <ChevronDown
                      className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-600 transition-transform duration-200 ${
                        showDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showDropdown && (
                    <div
                      className="absolute bg-white rounded-xl shadow-xl border border-gray-200 py-1"
                      style={{
                        zIndex: 99999,
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        left: "auto",
                        width: "calc(100vw - 16px)",
                        maxWidth: "280px",
                        minWidth: "200px",
                        transform: "translateX(calc(-100% + 100%))",
                        boxShadow:
                          "0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      {dropdownItems.map((item) => (
                        <button
                          key={item.key}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`CLICKED: ${item.key}`);
                            handleDropdownAction(item.key);
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                          }}
                          className="w-full flex items-center gap-2 px-3 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                          style={{
                            minHeight: "48px",
                            touchAction: "manipulation",
                            WebkitTapHighlightColor: "transparent",
                          }}
                        >
                          <item.icon className="w-4 h-4 text-gray-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Profile Menu - Solo Desktop */}
                <div className="hidden lg:block relative">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDropdown(!showDropdown);
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {agenzia?.nome_agenzia?.charAt(0) || "A"}
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${
                        showDropdown ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[220px] z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {agenzia?.nome_agenzia || "Agenzia"}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {agenzia?.email_referente}
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          setActiveBottomTab("impostazioni");
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">
                          Impostazioni
                        </span>
                      </button>

                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          setShowAssistanceModal(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                      >
                        <HelpCircle className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">
                          Assistenza
                        </span>
                      </button>

                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-red-50 transition-colors text-red-600"
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-24">
        {renderContent()}
      </div>

      {/* Instagram-style Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden">
        <div
          className="flex items-center justify-between px-1 py-1"
          style={{
            height: "60px",
            paddingBottom: "env(safe-area-inset-bottom, 4px)",
            maxWidth: "100vw",
            overflow: "hidden",
          }}
        >
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = activeBottomTab === item.key;

            return (
              <button
                key={item.key}
                onClick={() => {
                  setActiveBottomTab(item.key);
                  // Update breadcrumb based on navigation
                  switch (item.key) {
                    case "home":
                      setCurrentBreadcrumb("Dashboard");
                      break;
                    case "match":
                      setCurrentBreadcrumb("Dashboard > Match");
                      break;
                    case "formazione":
                      setCurrentBreadcrumb("Dashboard > Formazione & Team");
                      break;
                    case "messaggi":
                      setCurrentBreadcrumb("Dashboard > Messaggi");
                      break;
                    case "candidati":
                      setCurrentBreadcrumb("Dashboard > Candidati");
                      break;
                    case "notifiche":
                      setCurrentBreadcrumb("Dashboard > Notifiche");
                      break;
                    case "posts":
                      setCurrentBreadcrumb("Dashboard > I tuoi Post");
                      break;
                    case "abbonamento":
                      setCurrentBreadcrumb("Dashboard > Abbonamento");
                      break;
                    default:
                      setCurrentBreadcrumb("Dashboard");
                  }
                }}
                className={`flex flex-col items-center justify-center transition-all duration-200 min-w-0 flex-1 rounded-lg p-1 mx-0.5 ${
                  active
                    ? "text-blue-600 transform scale-105"
                    : "text-gray-400 hover:text-gray-600 active:scale-95"
                }`}
                style={{
                  minHeight: "44px",
                  maxWidth: "60px",
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <Icon
                  className={`transition-all duration-200 mb-0.5 ${
                    active
                      ? "w-4 h-4 text-blue-600"
                      : item.key === "posts"
                      ? "w-4 h-4 text-gray-400 hover:text-transparent hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 hover:bg-clip-text"
                      : "w-4 h-4 text-gray-400"
                  }`}
                  strokeWidth={active ? 2.5 : 1.8}
                />
                <span
                  className={`text-xs font-medium leading-tight text-center transition-all duration-200 truncate ${
                    active ? "text-blue-600" : "text-gray-400"
                  }`}
                  style={{ fontSize: "9px", maxWidth: "50px" }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Overlay for dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowDropdown(false)}
        />
      )}

      {/* Modal Contatta Assistenza */}
      {showAssistanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col"
            style={{ minHeight: "400px" }}
          >
            <div className="p-6 flex-1 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Contatta Assistenza</h3>
                <button
                  onClick={() => setShowAssistanceModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrivi il tuo problema o richiesta:
                  </label>
                  <textarea
                    value={assistanceMessage}
                    onChange={(e) => setAssistanceMessage(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
                    placeholder="Scrivi qui la tua richiesta di assistenza..."
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowAssistanceModal(false)}
                    className="flex-1"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSendAssistanceMessage}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!assistanceMessage.trim()}
                  >
                    Invia Richiesta
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Chat Conversazione */}
      {showChatModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col"
            style={{ minHeight: "400px" }}
          >
            <div className="p-6 flex-1 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  Conversazione con {selectedMessage.da}
                </h3>
                <button
                  onClick={() => setShowChatModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Messaggio originale */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <strong className="text-sm">{selectedMessage.da}</strong>
                    <span className="text-xs text-gray-500">
                      {selectedMessage.tempo}
                    </span>
                  </div>
                  <p className="text-sm">{selectedMessage.preview}</p>
                </div>

                {/* Area risposta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    La tua risposta:
                  </label>
                  <textarea
                    value={chatReply}
                    onChange={(e) => setChatReply(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Scrivi la tua risposta..."
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowChatModal(false)}
                    className="flex-1"
                  >
                    Chiudi
                  </Button>
                  <Button
                    onClick={handleSendChatReply}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!chatReply.trim()}
                  >
                    Invia Risposta
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestione Abbonamento */}
      {showSubscriptionModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col"
            style={{ minHeight: "400px" }}
          >
            <div className="p-6 flex-1 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  Conferma Piano {selectedPlan.name}
                </h3>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">
                    Piano: {selectedPlan.name}
                  </h4>
                  <p
                    className="text-2xl font-bold mb-2"
                    style={{ color: selectedPlan.color }}
                  >
                    {selectedPlan.price} {selectedPlan.period}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    Durata: {selectedPlan.duration}
                  </p>
                  <p className="text-sm text-green-700 font-medium">
                    {selectedPlan.guarantee}
                  </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h5 className="font-medium mb-2">📋 Cosa è incluso:</h5>
                  <ul className="text-sm space-y-1">
                    {selectedPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSubscriptionModal(false)}
                    className="flex-1"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={() => {
                      toast.success(
                        `Piano ${selectedPlan.name} attivato con successo!`
                      );
                      setShowSubscriptionModal(false);
                    }}
                    className="flex-1"
                    style={{ backgroundColor: selectedPlan.color }}
                  >
                    Conferma e Paga
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente per il cambio password
const ChangePasswordSection = ({ agenziaId }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validazioni
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Tutti i campi sono obbligatori");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Le password non corrispondono");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("La nuova password deve essere di almeno 8 caratteri");
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      toast.error("La password deve contenere almeno una lettera maiuscola");
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      toast.error("La password deve contenere almeno una lettera minuscola");
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      toast.error("La password deve contenere almeno un numero");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/agencies/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agency_id: agenziaId,
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password cambiata con successo!");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(data.detail || "Errore nel cambio password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Errore nel cambio password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password Attuale *
        </label>
        <input
          type={showPasswords ? "text" : "password"}
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nuova Password *
        </label>
        <input
          type={showPasswords ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Minimo 8 caratteri, una maiuscola, una minuscola e un numero
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Conferma Nuova Password *
        </label>
        <input
          type={showPasswords ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="showPasswords"
          checked={showPasswords}
          onChange={(e) => setShowPasswords(e.target.checked)}
          className="rounded"
        />
        <label htmlFor="showPasswords" className="text-sm text-gray-700">
          Mostra password
        </label>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700"
      >
        {loading ? "Cambiando..." : "Cambia Password"}
      </Button>
    </form>
  );
};

export default DashboardAgenzia;
